import {
  Accordion,
  AccordionItem,
  Input,
  Tab,
  Tabs,
  Textarea
} from '@heroui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  isTestCaseEditorValid,
  testCaseEditorSchema,
  type TestCaseEditorFormValues
} from './testCaseEditorSchema';

import type { DatasetEval } from '~/api/experiments/types';
import i18n from '~/common/helpers/i18n';

export type TestCaseEditorValues = TestCaseEditorFormValues;

type TestCaseEditorProps = {
  values: TestCaseEditorValues;
  formKey: string;
  datasetEvals?: DatasetEval[];
  onChange: (values: TestCaseEditorValues) => void;
  onValidityChange?: (isValid: boolean) => void;
  nameError?: string | null;
  isDisabled?: boolean;
};

export function TestCaseEditor({
  values,
  formKey,
  datasetEvals = [],
  onChange,
  onValidityChange,
  nameError = null,
  isDisabled = false
}: TestCaseEditorProps) {
  const {
    control,
    reset,
    setError,
    clearErrors,
    watch,
    formState: { errors, isValid }
  } = useForm<TestCaseEditorFormValues>({
    resolver: zodResolver(testCaseEditorSchema),
    mode: 'onChange',
    defaultValues: values
  });

  useEffect(() => {
    reset(values);
    // Re-seed only when the parent intentionally remounts editor context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey, reset]);

  useEffect(() => {
    const subscription = watch(formValues => {
      onChange(formValues as TestCaseEditorFormValues);
    });
    return () => subscription.unsubscribe();
  }, [watch, onChange]);

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  useEffect(() => {
    if (nameError) {
      setError('name', { type: 'manual', message: nameError });
      return;
    }

    clearErrors('name');
  }, [nameError, setError, clearErrors]);

  const watchedVariables = watch('variables') ?? values.variables;
  const variableEntries = Object.entries(watchedVariables);
  const nameFieldError = errors.name?.message ?? nameError ?? undefined;

  return (
    <div
      className='flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-auto pt-2'
      data-testid='monitor-agent-logs-collect-test-case-editor'
    >
      <div className='relative z-10 shrink-0'>
        <Controller
          name='name'
          control={control}
          render={({ field }) => (
            <Input
              label={i18n.t(
                'scenes.build.execution.experiments.editor.experiment_name_label'
              )}
              labelPlacement='outside'
              placeholder={i18n.t(
                'scenes.build.execution.experiments.editor.experiment_name_placeholder'
              )}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              isDisabled={isDisabled}
              isInvalid={Boolean(nameFieldError)}
              errorMessage={nameFieldError}
              classNames={{
                base: 'relative z-10',
                label: 'text-label-md text-on-surface',
                inputWrapper: 'border border-outline bg-surface',
                errorMessage: 'relative z-10'
              }}
              data-testid='monitor-agent-logs-collect-test-case-name'
            />
          )}
        />
      </div>

      <Tabs
        aria-label={i18n.t('scenes.monitor.agentLogs.collect.editorTabs')}
        variant='solid'
        classNames={{
          base: 'relative z-0 min-w-0',
          tabList:
            'relative overflow-hidden bg-surface-container-low p-1.5 rounded-md',
          cursor: 'bg-surface shadow-sm',
          tab: 'px-2 py-1',
          panel: 'relative z-0'
        }}
        data-testid='monitor-agent-logs-collect-editor-tabs'
      >
        <Tab
          key='variables'
          title={i18n.t(
            'scenes.build.execution.experiments.editor.variables_section',
            { count: variableEntries.length }
          )}
        >
          <div className='pt-3.5'>
            {variableEntries.length === 0 ? (
              <div
                className='flex flex-col items-center justify-center rounded-lg border border-dashed border-outline py-12'
                data-testid='monitor-agent-logs-collect-no-variables'
              >
                <p className='text-body-regular-md text-on-surface-variant'>
                  {i18n.t(
                    'scenes.build.execution.experiments.editor.no_variables'
                  )}
                </p>
              </div>
            ) : (
              <Accordion
                selectionMode='multiple'
                defaultExpandedKeys={variableEntries.map(([key]) => key)}
                className='flex flex-col gap-3.5 px-0'
              >
                {variableEntries.map(([variable]) => (
                  <AccordionItem
                    key={variable}
                    aria-label={variable}
                    title={
                      <span className='font-mono text-title-sm text-on-surface'>
                        {variable}
                      </span>
                    }
                    classNames={{
                      base: 'rounded-lg border border-outline px-4',
                      trigger: 'py-4',
                      content: 'pb-4'
                    }}
                    data-testid={`monitor-agent-logs-collect-variable-${variable}`}
                  >
                    <Controller
                      name={`variables.${variable}`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <Textarea
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          isDisabled={isDisabled}
                          isRequired
                          isInvalid={Boolean(fieldState.error)}
                          errorMessage={fieldState.error?.message}
                          minRows={6}
                          placeholder={i18n.t(
                            'scenes.build.execution.experiments.editor.value_placeholder'
                          )}
                          classNames={{
                            inputWrapper:
                              'bg-[rgba(242,242,242,0.5)] border-transparent'
                          }}
                          data-testid={`monitor-agent-logs-collect-variable-value-${variable}`}
                        />
                      )}
                    />
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </Tab>
        <Tab
          key='evaluations'
          title={i18n.t('scenes.monitor.agentLogs.collect.evaluationsTab')}
        >
          <div className='pt-3.5'>
            {datasetEvals.length === 0 ? (
              <div
                className='flex flex-col items-center justify-center rounded-lg border border-dashed border-outline py-12'
                data-testid='monitor-agent-logs-collect-evaluations-empty'
              >
                <p className='text-body-regular-md text-on-surface-variant'>
                  {i18n.t('scenes.monitor.agentLogs.collect.evaluationsEmpty')}
                </p>
              </div>
            ) : (
              <Accordion
                selectionMode='multiple'
                defaultExpandedKeys={datasetEvals.map(item => item.evalId)}
                className='flex flex-col gap-3.5 px-0'
                data-testid='monitor-agent-logs-collect-evaluations'
              >
                {datasetEvals.map(datasetEval => (
                  <AccordionItem
                    key={datasetEval.evalId}
                    aria-label={datasetEval.eval.name}
                    title={
                      <div className='flex flex-col gap-1'>
                        <span className='text-title-sm text-on-surface'>
                          {datasetEval.eval.name}
                        </span>
                        <span className='text-body-regular-sm text-on-surface-variant'>
                          {datasetEval.eval.scorer} • {datasetEval.eval.variant}
                        </span>
                      </div>
                    }
                    classNames={{
                      base: 'rounded-lg border border-outline px-4',
                      trigger: 'py-4',
                      content: 'pb-4'
                    }}
                    data-testid={`monitor-agent-logs-collect-eval-${datasetEval.evalId}`}
                  >
                    <Controller
                      name={`expectedResults.${datasetEval.evalId}`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <Textarea
                          label={i18n.t(
                            'scenes.monitor.agentLogs.collect.expectedResultLabel'
                          )}
                          labelPlacement='outside'
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          isDisabled={
                            isDisabled || datasetEval.eval.enabled === false
                          }
                          isInvalid={Boolean(fieldState.error)}
                          errorMessage={fieldState.error?.message}
                          minRows={6}
                          placeholder={i18n.t(
                            'scenes.monitor.agentLogs.collect.expectedResultPlaceholder'
                          )}
                          classNames={{
                            label: 'text-label-md text-on-surface',
                            inputWrapper:
                              'bg-[rgba(242,242,242,0.5)] border-transparent'
                          }}
                          data-testid={`monitor-agent-logs-collect-eval-value-${datasetEval.evalId}`}
                        />
                      )}
                    />
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </Tab>
      </Tabs>
    </div>
  );
}

export { isTestCaseEditorValid };
export type { TestCaseEditorFormValues };
